import React, { FC, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CommandResult } from 'src/core/stores/types';
import { CreateGroupItem, GroupItem, GroupItemStore, GroupSummaryStore, GroupType } from 'src/stores/groups-store';
import { container } from 'src/inversify.config';
import { Alert, Button, Col, Input, Row, Spin, Tabs, Form } from 'antd';
import { isEmailOrGuid, nameof } from 'src/core/utils/object';
import OrganizationsSelect from 'src/common/components/business/organization-select';
import NewItemModal from 'src/core/ui/newitemmodal';
import UserAutocomplete from 'src/common/components/business/user-autocomplete';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import LicenseAttributesComponent from '../licenses/license-attributes/license-attributes';
import RoleAssignmentAttributesComponent from '../role-assignments/role-attributes/role-attributes';
import { getUnlimitedLicenseAttributes, LicenseCreationAttributesStore, NewLicensCreationAttributes } from 'src/stores/licensing-store';
import { IdentityType } from 'src/common/types';
import { LicenseApproverRoleName, LicenseManagerRoleName } from 'src/stores/roles-store';
import { guid } from 'odata-query';
import { CreateRoleAssignmentItem, RoleAssignmentItemStore } from 'src/stores/role-assignments-store';
import { OrganizationItemStore, ORGANIZATIONS_MEMBER_LIMIT } from 'src/stores/organizations-store';
import { RuleObject } from 'antd/es/form';
import FormItem from 'antd/es/form/FormItem';
import RolesSelect from 'src/common/components/business/roles-select';
import { curateString } from 'src/core/utils/object';

interface NewGroupDialogProps {
  onClose: (response?: CommandResult<GroupItem>) => void;
  groupType: GroupType;
}

const NewGroupDialog: FC<NewGroupDialogProps> = ({ onClose, groupType }) => {
  const { t } = useTranslation();

  const [form] = Form.useForm();
  const [customError, setCustomError] = useState('');

  const newGroupStore = container.get(GroupItemStore);
  const newGroupState = newGroupStore.state;

  const groupSummaryStore = container.get(GroupSummaryStore);

  const newRoleAssignmentStore = container.get(RoleAssignmentItemStore);
  const licenseCreationAttributesStore = useMemo(() => container.get(LicenseCreationAttributesStore), []);
  const disabledUsersRef = useRef<string[]>([]);
  const organizationStore = container.get(OrganizationItemStore);
  const organizationState = organizationStore.state;
  const [currentOrganizationMembers, setCurrentOrganizationMembers] = useState<string[]>([]);
  const [groupUserCount, setGroupUserCount] = useState<number>(0);
  const [formUsers, setFormUsers] = useState<{ label: string; value: string }[]>([]);
  const [organizationId, setOrganizationId] = useState<string | undefined>();
  const [selectedRoles, setSelectedRoles] = useState<{ value: string; label: string }[]>([]);

  const onNewItem = async () => {
    let item: any;
    try {
      item = (await form.validateFields()) as any;
    } catch {
      setCustomError(t('CheckGroupInfoAndUsersTabs'));
      return;
    }

    if (groupType === GroupType.License && (!organizationId || selectedRoles.length === 0)) return;

    const groupName = curateString(item.name);

    const duplicatedGroups = await groupSummaryStore.getGroups(
      {
        filter: { Name: groupName, OrganizationId: guid(organizationId) },
      },
      [groupType]
    );

    if (duplicatedGroups?.length) {
      setCustomError(t('ThereIsAlreadyAGroupWithThisName'));
      return;
    }

    setCustomError('');

    const body = {
      name: groupName,
      organizationId: item.organization.value,
      members: (item.members || []).map((x: any) => x.user.value),
      groupType: groupType,
    } as CreateGroupItem;

    let response = await newGroupStore.create(body);
    const groupId = response?.identity;

    if (groupType === GroupType.License && groupId) {
      let licenseAttributes: NewLicensCreationAttributes = {
        allowedDevices: item?.allowedDevices ?? [],
        canCreateUnlimitedLicense: item?.canCreateUnlimitedLicense ?? false,
        identityId: groupId,
        identityType: IdentityType.Group,
        maximumLicenseDueDate: item?.maximumLicenseDueDate,
        needsSignOff: item?.needsSignOff ?? false,
        maxLicensesPeriodInDays: item?.maxLicensesPeriodInDays,
        maxNumberOfLicensesPerMonth: item.maxNumberOfLicensesPerMonth,
        canCreateRecurrentLicenses: item.canCreateRecurrentLicenses ?? false,
        scopeId: organizationId!,
      };

      const unlimitedLicenseDefaults = getUnlimitedLicenseAttributes();

      if (licenseAttributes.canCreateUnlimitedLicense) {
        licenseAttributes.maximumLicenseDueDate = unlimitedLicenseDefaults.untilDate;
        licenseAttributes.maxLicensesPeriodInDays = unlimitedLicenseDefaults.maxPeriodInDays;
      }

      const expirationDate = item.expirationDate;

      const postData: CreateRoleAssignmentItem = {
        identityId: groupId,
        identityType: IdentityType.Group,
        roles: selectedRoles.map((v) => ({ role: v.label, expirationDate: expirationDate, roleId: v.value })),
        scopeId: organizationId!,
      };

      await newRoleAssignmentStore.create(postData);
      if (selectedRoles.some((v) => v.label === LicenseManagerRoleName)) {
        await licenseCreationAttributesStore.post(licenseAttributes);
      }
    }

    if (response?.isSuccess) {
      form.resetFields();
      onClose(response);
    }
  };

  const loadDisabledUsers = async (organizationId: string) => {
    await organizationStore.load(organizationId);
    setCurrentOrganizationMembers((organizationState?.item?.value?.members ?? [])?.map((v) => v.userId) ?? []);
    if (groupType === GroupType.License) {
      const users = await newGroupStore.getUsersAlreadyOnGroupType(organizationId, GroupType.License);
      if (users) disabledUsersRef.current = users;
    }
  };

  return (
    <NewItemModal
      className="figma-modal"
      title={t('AddNewGroup')}
      onNewItem={onNewItem}
      onClose={onClose}
      width={600}
      onOkButtonIsDisabled={
        newGroupState.isBusy.get() ||
        formUsers.filter((formUser) => !currentOrganizationMembers.includes(formUser?.value)).length + currentOrganizationMembers.length >
          ORGANIZATIONS_MEMBER_LIMIT
      }
      onCancelButtonIsDisabled={newGroupState.isBusy.get()}
      closable={!newGroupState.isBusy.get()}
      maskClosable={!newGroupState.isBusy.get()}
      okText={t('AddGroup')}
      errorMessage={newGroupState.errorMessage}
      body={
        <>
          {(customError || '').length !== 0 && <Alert type="error" message={customError} closable onClose={() => setCustomError('')} />}
          <Spin spinning={newGroupState.isBusy.get() || organizationState.isBusy.get()}>
            {formUsers.filter((formUser) => formUser?.value && !currentOrganizationMembers.includes(formUser?.value)).length +
              currentOrganizationMembers.length >
              ORGANIZATIONS_MEMBER_LIMIT && (
              <Alert
                type="error"
                message={t('NewGroupUserCountErrorParam')
                  .replace('{0}', `${ORGANIZATIONS_MEMBER_LIMIT}`)
                  .replace('{1}', `${currentOrganizationMembers.length}`)
                  .replace(
                    '{2}',
                    `${formUsers.filter((formUser) => formUser?.value && !currentOrganizationMembers.includes(formUser?.value)).length}`
                  )}
              />
            )}
            {formUsers.filter((formUser) => !currentOrganizationMembers.includes(formUser?.value) && formUser?.value).length > 0 && (
              <Alert
                type="warning"
                message={
                  <>
                    {t('TheFollowingUsersWillBeAddedToTheOrganization')}
                    <br />
                    <br />
                    {formUsers
                      .filter((formUser) => !currentOrganizationMembers.includes(formUser?.value) && formUser?.label)
                      .map((v) => (
                        <>
                          - {v?.label}
                          <br />
                        </>
                      ))}
                  </>
                }
              />
            )}
            <Form form={form} layout="vertical" autoComplete="off">
              <Tabs defaultActiveKey="1">
                <Tabs.TabPane className="ant-tabs-tabpane-without-border" tab={t('GroupInfo')} key="1">
                  <Form.Item
                    name={nameof<CreateGroupItem>('name')}
                    rules={[
                      { required: true, message: t('PleaseEnterName') },
                      () => ({
                        validator(_, value: string) {
                          if (value && value.trim().length === 0) return Promise.reject(t('PleaseEnterName'));
                          return Promise.resolve();
                        },
                      }),
                      () => ({
                        validator: (ruleObj: RuleObject, value: string) => {
                          const maxLenght = 128;
                          if (value === undefined) return Promise.resolve();
                          if (value.length > maxLenght) {
                            return Promise.reject(t('TextFieldLimitError').replace('{0}', t('Name')).replace('{1}', `${maxLenght}`));
                          } else return Promise.resolve();
                        },
                      }),
                    ]}
                  >
                    <Input autoComplete="off" allowClear placeholder={t('Name')} />
                  </Form.Item>
                  <Form.Item name="organization" rules={[{ required: true, message: t('PleaseSelect') }]}>
                    <OrganizationsSelect
                      onChange={(item) => {
                        loadDisabledUsers(item?.value);
                        setOrganizationId(item?.value);
                      }}
                      onlyPartners={groupType === GroupType.License}
                    />
                  </Form.Item>

                  {groupType === GroupType.License && (
                    <FormItem name={'roles'} rules={[{ required: true, message: t('PleaseSelect') }]}>
                      <RolesSelect
                        filterNames={groupType === GroupType.License ? [LicenseManagerRoleName, LicenseApproverRoleName] : undefined}
                        organizationId={organizationId}
                        onChange={(v) => {
                          setSelectedRoles(v);
                        }}
                      />
                    </FormItem>
                  )}

                  {groupType === GroupType.License && (
                    <>
                      <RoleAssignmentAttributesComponent onChange={() => {}} />
                      {selectedRoles.some((v) => v.label === LicenseManagerRoleName) && (
                        <LicenseAttributesComponent form={form} onChange={() => {}} />
                      )}
                    </>
                  )}
                </Tabs.TabPane>
                <Tabs.TabPane forceRender className="ant-tabs-tabpane-without-border" tab={t('GroupMembers')} key="2">
                  <Form.List name="members">
                    {(fields, { add, remove }) => (
                      <>
                        <div id="new-organization-users-container" style={{ maxHeight: '40vh', overflowX: 'auto' }}>
                          {fields.map(({ key, name: fieldIndx, ...restField }) => (
                            <Row key={key}>
                              <Col span={20}>
                                <Form.Item
                                  {...restField}
                                  style={{ marginRight: 10 }}
                                  name={[fieldIndx, 'user']}
                                  dependencies={['organization']}
                                  rules={[
                                    () => ({
                                      required: true,
                                      validator(_, value) {
                                        if (value?.value == null) return Promise.reject(t('SelectOrInviteWarning'));
                                        if (!isEmailOrGuid(value.value)) return Promise.reject(t('InvalidEmailWarning'));
                                        return Promise.resolve();
                                      },
                                    }),
                                    () => ({
                                      validator(_, value) {
                                        if (!value?.value) return Promise.resolve();
                                        if (disabledUsersRef.current.includes(value.value))
                                          return Promise.reject(t('UserAlreadyIsLicenseManagerError'));
                                        return Promise.resolve();
                                      },
                                    }),
                                    (formInstance) => ({
                                      message: t('DuplicatedUserWarning'),
                                      validator(_, formItem) {
                                        const items: { user: { label: string; value: string } }[] = formInstance.getFieldValue('members');

                                        if (formItem === undefined) return Promise.resolve();

                                        if (items.filter((item) => item && item.user.value === formItem.value).length > 1) {
                                          return Promise.reject();
                                        } else {
                                          return Promise.resolve();
                                        }
                                      },
                                    }),
                                  ]}
                                >
                                  <UserAutocomplete
                                    onChange={(user) => {
                                      setFormUsers(
                                        form.getFieldsValue().members.map((m: { user: { label: string; value: string } | undefined }) => m?.user)
                                      );
                                    }}
                                  />
                                </Form.Item>
                              </Col>
                              <Col span={2} style={{ textAlign: 'center' }}>
                                <MinusCircleOutlined
                                  style={{ fontSize: 20 }}
                                  onClick={() => {
                                    setGroupUserCount(groupUserCount - 1);
                                    remove(fieldIndx);
                                    setFormUsers(
                                      form.getFieldsValue().members.map((m: { user: { label: string; value: string } | undefined }) => m?.user)
                                    );
                                  }}
                                />
                              </Col>
                            </Row>
                          ))}
                        </div>
                        <Form.Item>
                          <Button
                            type="dashed"
                            onClick={() => {
                              add();
                              setGroupUserCount(groupUserCount + 1);
                            }}
                            block
                            icon={<PlusOutlined />}
                          >
                            {t('AddUser')}
                          </Button>
                        </Form.Item>
                      </>
                    )}
                  </Form.List>
                </Tabs.TabPane>
              </Tabs>
            </Form>
          </Spin>
        </>
      }
    />
  );
};

export default NewGroupDialog;
